import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  Save,
  Building,
  Phone,
  Mail,
  Calendar,
  BookOpen,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  Department,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
} from "../../types/department";
import { getBranches } from "../../services/branches";
import { Branch } from "../../types/branch";
import { AuthUser } from "../../types/auth";
import { getCourses } from "../../services/courses";
import { Course } from "../../types/course";
import { getTeachers } from "../../services/teachers";

interface DepartmentFormProps {
  department?: Department;
  onSubmit: (
    data: CreateDepartmentRequest | UpdateDepartmentRequest
  ) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
  user?: AuthUser | null;
}

const DepartmentForm: React.FC<DepartmentFormProps> = ({
  department,
  onSubmit,
  onClose,
  loading = false,
  user,
}) => {
  // Determine if user is branch admin or regular admin (not superadmin)
  const isBranchAdmin =
    (user?.roles?.includes("branchadmin") || user?.roles?.includes("admin")) &&
    !user?.roles?.includes("superadmin");
  const userBranchId = user?.branchId;
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    headOfDepartment: "",
    contactInfo: {
      phone: "",
      email: "",
    },
    branchId: isBranchAdmin ? userBranchId || "" : "",
    establishedDate: "",
    programs: [] as string[],
    whatsappGroupLink: "",
    isActive: true,
  });

  const [newProgram, setNewProgram] = useState("");
  const [branches, setBranches] = useState<Branch[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch branches for dropdown (only for superadmins)
  useEffect(() => {
    if (!isBranchAdmin) {
      const fetchBranches = async () => {
        try {
          const response = await getBranches();
          if (response.success) {
            setBranches(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch branches:", error);
        }
      };
      fetchBranches();
    }
  }, [isBranchAdmin]);

  // Fetch courses for this department (only for existing departments)
  useEffect(() => {
    if (department?._id) {
      const fetchCourses = async () => {
        try {
          const response = await getCourses({
            departmentId: department._id,
            limit: 100,
          });
          if (response.success) {
            setCourses(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch courses:", error);
        }
      };
      fetchCourses();
    }
  }, [department]);

  // Fetch teachers for head of department selection
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await getTeachers({
          limit: 100,
        });
        if (response.success) {
          console.log("Teachers data:", response.data); // Debug log
          setTeachers(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch teachers:", error);
      }
    };

    fetchTeachers();
  }, []);

  // Initialize form data when editing
  useEffect(() => {
    if (department) {
      setFormData({
        name: department.name || "",
        code: department.code || "",
        description: department.description || "",
        headOfDepartment: department.headOfDepartment || "",
        contactInfo: {
          phone: department.contactInfo?.phone || "",
          email: department.contactInfo?.email || "",
        },
        branchId: isBranchAdmin
          ? userBranchId || ""
          : department.branchId || "",
        establishedDate: department.establishedDate
          ? new Date(department.establishedDate).toISOString().split("T")[0]
          : "",
        programs: department.programs || [],
        whatsappGroupLink: department.whatsappGroupLink || "",
        isActive: department.isActive ?? true,
      });
    } else {
      // Reset form for new department
      setFormData({
        name: "",
        code: "",
        description: "",
        headOfDepartment: "",
        contactInfo: {
          phone: "",
          email: "",
        },
        branchId: isBranchAdmin ? userBranchId || "" : "",
        establishedDate: "",
        programs: [] as string[],
        whatsappGroupLink: "",
        isActive: true,
      });
    }
  }, [department, isBranchAdmin, userBranchId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Department name is required";
    }

    if (!formData.code.trim()) {
      newErrors.code = "Department code is required";
    } else if (!/^[A-Z]{2,10}$/.test(formData.code)) {
      newErrors.code = "Code must be 2-10 uppercase letters";
    }

    // Only validate branchId for superadmins
    if (!isBranchAdmin && !formData.branchId) {
      newErrors.branchId = "Branch is required";
    }

    // For branch admins, ensure they have a valid branch assigned
    if (isBranchAdmin && !userBranchId) {
      newErrors.branchId =
        "Your branch information is not available. Please contact administrator.";
    }

    if (
      formData.contactInfo.email &&
      !/\S+@\S+\.\S+/.test(formData.contactInfo.email)
    ) {
      newErrors.email = "Invalid email format";
    }

    if (
      formData.contactInfo.phone &&
      !/^[\+]?[0-9\-\(\)\s]+$/.test(formData.contactInfo.phone)
    ) {
      newErrors.phone = "Invalid phone format";
    }

    // Validate WhatsApp group link
    if (
      formData.whatsappGroupLink &&
      !/^(https?:\/\/)?(chat\.whatsapp\.com|wa\.me)\/[^\s]+$/.test(
        formData.whatsappGroupLink
      )
    ) {
      newErrors.whatsappGroupLink = "Invalid WhatsApp group link format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: any = {
        ...formData,
        establishedDate: formData.establishedDate
          ? new Date(formData.establishedDate)
          : undefined,
        contactInfo: {
          phone: formData.contactInfo.phone || undefined,
          email: formData.contactInfo.email || undefined,
        },
      };

      // Remove empty contact info
      if (!submitData.contactInfo.phone && !submitData.contactInfo.email) {
        delete submitData.contactInfo;
      }

      // Remove empty headOfDepartment
      if (
        !submitData.headOfDepartment ||
        submitData.headOfDepartment.trim() === ""
      ) {
        delete submitData.headOfDepartment;
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const addProgram = () => {
    if (newProgram.trim() && !formData.programs.includes(newProgram.trim())) {
      setFormData((prev) => ({
        ...prev,
        programs: [...prev.programs, newProgram.trim()],
      }));
      setNewProgram("");
    }
  };

  const removeProgram = (program: string) => {
    setFormData((prev) => ({
      ...prev,
      programs: prev.programs.filter((p) => p !== program),
    }));
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleContactInfoChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [field]: value,
      },
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Building className="h-5 w-5" />
            {department ? "Edit Department" : "Create Department"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter department name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Department Code *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) =>
                  handleInputChange("code", e.target.value.toUpperCase())
                }
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.code ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="e.g., CS, MATH, ENG"
                maxLength={10}
              />
              {errors.code && (
                <p className="mt-1 text-sm text-red-600">{errors.code}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter department description"
            />
          </div>

          {/* Branch Selection - Only show for users who are NOT branch admins or regular admins (i.e., superadmins only) */}
          {!isBranchAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch *
              </label>
              <select
                value={formData.branchId}
                onChange={(e) => handleInputChange("branchId", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.branchId ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="">Select a branch</option>
                {branches.map((branch) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name} ({branch.abbreviation})
                  </option>
                ))}
              </select>
              {errors.branchId && (
                <p className="mt-1 text-sm text-red-600">{errors.branchId}</p>
              )}
            </div>
          )}

          {/* Branch Display for Branch Admins */}
          {isBranchAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch
              </label>
              <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                {branches.find((branch) => branch._id === userBranchId)?.name ||
                  "Your Branch"}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Departments can only be created for your assigned branch
              </p>
            </div>
          )}

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">
              Contact Information (Optional)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="h-4 w-4 inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  value={formData.contactInfo.email}
                  onChange={(e) =>
                    handleContactInfoChange("email", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="department@university.edu"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="h-4 w-4 inline mr-1" />
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.contactInfo.phone}
                  onChange={(e) =>
                    handleContactInfoChange("phone", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.phone ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="+1 (555) 123-4567"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* WhatsApp Group Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp Group Link (Optional)
            </label>
            <input
              type="url"
              value={formData.whatsappGroupLink}
              onChange={(e) =>
                handleInputChange("whatsappGroupLink", e.target.value)
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.whatsappGroupLink ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="https://chat.whatsapp.com/..."
            />
            <p className="mt-1 text-sm text-gray-500">
              Enter the WhatsApp group invite link for this department
            </p>
            {errors.whatsappGroupLink && (
              <p className="mt-1 text-sm text-red-600">
                {errors.whatsappGroupLink}
              </p>
            )}
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Calendar className="h-4 w-4 inline mr-1" />
                Established Date
              </label>
              <input
                type="date"
                value={formData.establishedDate}
                onChange={(e) =>
                  handleInputChange("establishedDate", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Head of Department
              </label>
              <select
                value={formData.headOfDepartment}
                onChange={(e) =>
                  handleInputChange("headOfDepartment", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a teacher (optional)</option>
                {teachers.map((teacher) => {
                  // Try multiple possible data structures
                  const firstName =
                    teacher.userId?.firstName ||
                    teacher.userInfo?.firstName ||
                    teacher.firstName ||
                    "";
                  const lastName =
                    teacher.userId?.lastName ||
                    teacher.userInfo?.lastName ||
                    teacher.lastName ||
                    "";
                  const displayName =
                    firstName && lastName
                      ? `${firstName} ${lastName}`
                      : teacher.name ||
                        teacher.userId?.name ||
                        teacher.userInfo?.name ||
                        "Unknown Teacher";
                  const teacherId =
                    teacher.userId?._id || teacher.userId || teacher._id;

                  return (
                    <option key={teacher._id} value={teacherId}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Select a teacher to be the head of this department
              </p>
            </div>
          </div>

          {/* Programs/Courses */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Programs Offered
            </label>

            {/* Display courses associated with this department */}
            {courses.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-600 mb-2">
                  Courses in this Department:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {courses.map((course) => (
                    <div
                      key={course._id}
                      className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <BookOpen className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-800">
                        {course.name} ({course.code})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manual program entry (optional) */}
            <div className="mb-2">
              <label className="block text-sm text-gray-600 mb-1">
                Additional Programs (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newProgram}
                  onChange={(e) => setNewProgram(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addProgram())
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter additional program name"
                />
                <button
                  type="button"
                  onClick={addProgram}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </button>
              </div>
            </div>

            {/* Display manually added programs */}
            {formData.programs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.programs.map((program, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {program}
                    <button
                      type="button"
                      onClick={() => removeProgram(program)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {courses.length === 0 && formData.programs.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                No programs or courses assigned yet. Add courses to this
                department to see them listed here.
              </p>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleInputChange("isActive", e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
              Department is active
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {loading
                ? "Saving..."
                : department
                ? "Update Department"
                : "Create Department"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartmentForm;
